import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";
import { Switch } from "./ui/switch";
import { Home, Loader2, DoorOpen, DoorClosed } from "lucide-react";
import { toast } from "sonner";
import { motion } from "motion/react";
import roomService, { Room } from "../services/room.service";

interface AdminRoomManagementProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdminRoomManagement({ open, onOpenChange }: AdminRoomManagementProps) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingRoomId, setSavingRoomId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchRooms();
    }
  }, [open]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const data = await roomService.getAllRooms();
      setRooms([...data].sort((a, b) => a.roomNumber - b.roomNumber));
    } catch (error: any) {
      toast.error(error.message || "Failed to load rooms");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (room: Room, makeOpen: boolean) => {
    setSavingRoomId(room._id);
    try {
      const updated = await roomService.updateRoomStatus(room._id, makeOpen ? "active" : "inactive");
      setRooms(prev => prev.map(r => (r._id === room._id ? { ...r, status: updated.status } : r)));
      toast.success(`Room ${room.roomNumber} ${makeOpen ? "opened" : "closed"} for booking`);
    } catch (error: any) {
      toast.error(error.message || "Failed to update room availability");
    } finally {
      setSavingRoomId(null);
    }
  };

  const openCount = rooms.filter(r => r.status === "active").length;
  const closedCount = rooms.length - openCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[92vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f1f3d] flex items-center gap-2">
            <Home className="w-6 h-6 text-[#d4af37]" />
            Room Availability
          </DialogTitle>
          <DialogDescription>
            Open or close individual rooms for booking. Closed rooms can't be booked by students.
          </DialogDescription>
        </DialogHeader>

        {!loading && rooms.length > 0 && (
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-700 border-green-200 flex items-center gap-1">
              <DoorOpen className="w-3 h-3" /> {openCount} open
            </Badge>
            <Badge className="bg-gray-100 text-gray-700 border-gray-200 flex items-center gap-1">
              <DoorClosed className="w-3 h-3" /> {closedCount} closed
            </Badge>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
            </div>
          ) : rooms.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Home className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No rooms found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {rooms.map((room) => {
                const isOpen = room.status === "active";
                return (
                  <motion.div
                    key={room._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className={`p-4 flex items-center justify-between ${isOpen ? "" : "bg-gray-50"}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#0f1f3d] flex items-center justify-center">
                          <Home className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-[#0f1f3d]">Room {room.roomNumber}</div>
                          <div className="text-xs text-gray-500">
                            Floor {room.floor} • {room.availableBeds}/{room.totalBeds} beds free
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Badge
                          className={isOpen
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-gray-200 text-gray-700 border-gray-300"}
                        >
                          {isOpen ? "Open" : "Closed"}
                        </Badge>
                        {savingRoomId === room._id ? (
                          <Loader2 className="w-4 h-4 animate-spin text-[#d4af37]" />
                        ) : (
                          <Switch
                            checked={isOpen}
                            onCheckedChange={(checked) => handleToggle(room, checked)}
                            aria-label={`Toggle availability for room ${room.roomNumber}`}
                          />
                        )}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
